/**
 * "Your latest changes aren't saved to your account" (FN-5).
 *
 * Three affordances, and only two of them resolve. Download runs IN the dialog
 * and leaves it open, because a download is a step you take BEFORE deciding,
 * not a decision: resolving on it would sign the user out on the strength of a
 * file the browser may never have written. Cancel returns to editing with the
 * collaboration session still connected.
 *
 * Composed from the kit rather than `createConfirmDialog`, which is two-button
 * by contract. Nothing here is hand-rolled: the overlay, focus trap, Escape
 * handling and buttons are all `components.js`.
 *
 * Note what this dialog never offers: a way to delete the local copy. When work
 * is unsynced the copy is KEPT either way — it is account-scoped and ownership
 * stamped (see `core/durability.js`), so keeping it is safe and losing it is
 * not recoverable.
 */

import { createModal, createButton, setButtonBusy } from './components.js';
import { reportError } from '../core/report-error.js';

/** Why the work is not in the cloud — each gets its own honest sentence. */
const REASON_COPY = Object.freeze({
  'never-in-cloud': 'This project has never been saved to your account.',
  unnamed: 'This project needs a name before it can be saved to your account.',
  'save-failed': "We couldn't save your latest changes to your account.",
  'save-timeout': 'Saving to your account is taking longer than expected.',
  offline: "You appear to be offline, so your latest changes haven't reached your account.",
  unknown: "We can't confirm your latest changes reached your account."
});

/**
 * @param {Object} opts
 * @param {string} opts.reason        — a key of REASON_COPY
 * @param {string} [opts.projectName]
 * @param {() => Promise<any>} opts.onDownload — runs in place; must not resolve the dialog
 * @returns {Promise<'signout'|'cancel'>}
 */
export function showUnsavedOnLogoutDialog({ reason = 'unknown', projectName = '', onDownload }) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (choice) => {
      if (settled) return;
      settled = true;
      resolve(choice);
      modal.close();
    };

    const modal = createModal({
      title: 'Sign out without saving?',
      size: 'sm',
      context: projectName ? { label: projectName } : undefined,
      // The dialog can hold an in-flight download; a stray backdrop click must
      // not dismiss it. Escape is still a deliberate cancel.
      dismissOnBackdrop: false,
      initialFocus: () => cancelBtn,
      onEscape: () => finish('cancel'),
      // A close from the header X is a cancel, not a sign-out. Guarded so the
      // programmatic close inside finish() cannot re-enter.
      onClose: () => finish('cancel')
    });

    const explain = document.createElement('p');
    explain.className = 'pb-modal-text';
    explain.textContent = REASON_COPY[reason] || REASON_COPY.unknown;

    const keep = document.createElement('p');
    keep.className = 'pb-modal-text pb-modal-text-dim';
    keep.textContent =
      'Signing out keeps this copy on this device for your account, so you can pick it up ' +
      'again next time you sign in here. Download a backup if you need it somewhere else.';

    modal.body.append(explain, keep);

    const downloadBtn = createButton({
      variant: 'outline',
      label: 'Download a backup (.zip)',
      labelClass: 'pb-btn-label',
      onClick: async () => {
        if (typeof onDownload !== 'function') return;
        setButtonBusy(downloadBtn, true, { busyLabel: 'Preparing…' });
        try {
          await onDownload();
        } catch (err) {
          // User-initiated and user-visible: the dialog stays open so the
          // choice is still theirs, and the toast says what failed.
          reportError('Sign out — backup', err, {
            user: 'toast',
            message: "Couldn't prepare the backup. Your work is still on this device."
          });
        } finally {
          setButtonBusy(downloadBtn, false);
        }
      }
    });

    const cancelBtn = createButton({
      variant: 'ghost',
      label: 'Cancel',
      labelClass: 'pb-btn-label',
      onClick: () => finish('cancel')
    });

    const signOutBtn = createButton({
      variant: 'primary',
      label: 'Sign out',
      labelClass: 'pb-btn-label',
      onClick: () => finish('signout')
    });

    modal.footer.append(downloadBtn, cancelBtn, signOutBtn);
  });
}
